/**
 * Shared Today / item-page helpers — V98-3-3..3-5.
 *
 *   useFocusAfter   focus the NEW state by id once it is in the DOM (after a
 *                   re-render or refetch), never <body> (R3 a11y).
 *   useAnnounce     a polite live region; messages are set after the dialog
 *                   that triggered them has closed (R3: toast after close).
 *   ActionDialog    native <dialog> + showModal (focus trap, Escape) with an
 *                   h2 title and the submit as the ONE primary in the layer.
 *
 * V98-3 review U9 / V98-3-7:
 *   - the footer is sticky: the body scrolls between the title and the
 *     actions, so Save stays visible at any height (900px included);
 *   - errors render INSIDE the dialog, under the title (role=alert);
 *   - an optional step line ("Step 2 of 2 · Who checks it") takes focus
 *     when the step changes, so a screen reader hears the new step;
 *   - focus goes back to the opener when the dialog closes (unless the
 *     caller moves it to a new state);
 *   - keyboard-safe on phones: the sheet's height follows `visualViewport`
 *     (CSS var --gp-vvh), so the on-screen keyboard never hides the footer.
 *
 * V98-4 review: `hideSubmit` (a read-only sheet has one exit, Close);
 * `initialFocus="title"` (a multi-field editor opens on its heading);
 * `headerBack` (a sub-step's way back is a header link, so the footer keeps
 * one exit); `pinned` (content kept visible above the footer, e.g. the
 * privilege-waiver acknowledgement).
 */
import { useCallback, useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { Alert, Button } from '@aigovops/design-system';
import type { Tone } from '../../lib/today-model';

/** A DOM-safe id for an item key ("policy:pol-1" → "policy_pol-1"). */
export const domId = (prefix: string, key: string) => `${prefix}-${key.replace(/[^a-zA-Z0-9_-]/g, '_')}`;

export function useFocusAfter(): (id: string) => void {
  const pending = useRef<string | null>(null);
  const [, bump] = useState(0);
  useEffect(() => {
    if (!pending.current) return;
    const el = document.getElementById(pending.current);
    if (el) {
      if (!el.hasAttribute('tabindex') && !/^(A|BUTTON|INPUT|SELECT|TEXTAREA)$/.test(el.tagName)) el.setAttribute('tabindex', '-1');
      el.focus();
      pending.current = null;
    }
  });
  return useCallback((id: string) => { pending.current = id; bump((n) => n + 1); }, []);
}

export function useAnnounce(): [ReactNode, (msg: string) => void] {
  const [msg, setMsg] = useState('');
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // V109-5 BU-10: a message still pending when the effect is torn down is re-sent when it sets up again
  // (StrictMode's mount → cleanup → mount keeps refs), so an announcement made while mounting is not lost.
  const pending = useRef<string | null>(null);
  const send = useCallback((m: string) => {
    pending.current = m;
    if (timer.current) clearTimeout(timer.current);
    // After the dialog has closed and focus has settled.
    timer.current = setTimeout(() => { pending.current = null; setMsg(m); }, 60);
  }, []);
  useEffect(() => {
    if (pending.current !== null) send(pending.current);
    return () => { if (timer.current) clearTimeout(timer.current); };
  }, [send]);
  const announce = useCallback((m: string) => {
    setMsg('');
    send(m);
  }, [send]);
  return [<p key="announce" role="status" aria-live="polite" className="gp-sr-only" data-announce>{msg}</p>, announce];
}

export function Chip({ text, tone = 'neutral', id }: { text: string; tone?: Tone | 'lock' | 'ok' | 'outline'; id?: string }) {
  return <span id={id} className={`gp-chip gp-chip--${tone}`}>{text}</span>;
}

/**
 * V98-5 review T5-H15: an in-page anchor that moves focus, not just the
 * scroll position — the target gets tabindex=-1 and focus (a screen reader
 * hears where it landed). `to` is an element id.
 */
export function JumpLink({ to, children, className }: { to: string; children: ReactNode; className?: string }) {
  return (
    <a href={`#${to}`} className={className} onClick={(e) => {
      const el = document.getElementById(to);
      if (!el) return;
      e.preventDefault();
      if (!el.hasAttribute('tabindex') && !/^(A|BUTTON|INPUT|SELECT|TEXTAREA)$/.test(el.tagName)) el.setAttribute('tabindex', '-1');
      el.scrollIntoView?.({ block: 'start' });
      el.focus();
    }}>{children}</a>
  );
}

/**
 * Track the visual viewport (iOS keyboard) while `active`: sets --gp-vvh (the
 * visible height) and --gp-vv-bottom (how much the keyboard covers) on `el`.
 */
export function useVisualViewport(el: HTMLElement | null, active: boolean): void {
  useEffect(() => {
    const vv = typeof window !== 'undefined' ? window.visualViewport : null;
    if (!el || !active || !vv) return;
    const update = () => {
      el.style.setProperty('--gp-vvh', `${Math.round(vv.height)}px`);
      el.style.setProperty('--gp-vv-bottom', `${Math.max(0, Math.round(window.innerHeight - vv.height - vv.offsetTop))}px`);
    };
    update();
    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    return () => {
      vv.removeEventListener('resize', update);
      vv.removeEventListener('scroll', update);
      el.style.removeProperty('--gp-vvh');
      el.style.removeProperty('--gp-vv-bottom');
    };
  }, [el, active]);
}

export function ActionDialog({
  open, title, submitLabel, onClose, onSubmit, busy, error, children, danger, step, submitDisabled, back, cancelLabel = 'Cancel',
  hideSubmit, initialFocus = 'first', headerBack, pinned, describedBy, secondary, subtitle,
}: {
  open: boolean;
  title: string;
  submitLabel: string;
  onClose: () => void;
  /** Return false to keep the dialog open (e.g. client validation failed). */
  onSubmit: () => Promise<boolean | void> | boolean | void;
  busy?: boolean;
  /** Shown inside the dialog, under the title. */
  error?: string | null;
  children: ReactNode;
  danger?: boolean;
  /** Multi-step dialogs: "Step n of m · label"; focus moves to it when n changes. */
  step?: { n: number; of: number; label: string };
  submitDisabled?: boolean;
  /** R3-U11: a multi-step dialog's way back to the previous step (shown in the footer, before Cancel). */
  back?: { label: string; onClick: () => void } | null;
  /** The dismiss button's label (e.g. "Keep it" when the submit itself is a cancellation). */
  cancelLabel?: string;
  /** No submit: a read-only sheet whose only exit is the dismiss button. */
  hideSubmit?: boolean;
  /** 'title': focus the heading on open (multi-field editors); 'first' (default): the first field. */
  initialFocus?: 'first' | 'title';
  /** A sub-step's way back, shown as a link in the header (the footer keeps one exit). */
  headerBack?: { label: string; onClick: () => void } | null;
  /** Kept visible between the scrolling body and the footer. */
  pinned?: ReactNode;
  /** Id of an inline reason the (aria-disabled) submit is described by. */
  describedBy?: string;
  /** A second, non-primary way to finish (e.g. "I fixed it" beside "Use as plan"); shown before Cancel. */
  secondary?: { label: string; onClick: () => void; busy?: boolean } | null;
  /** One short line under the title (e.g. how the item closes). */
  subtitle?: ReactNode;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const stepRef = useRef<HTMLParagraphElement>(null);
  const opener = useRef<Element | null>(null);
  const titleId = useRef(`dlg-${Math.random().toString(36).slice(2, 9)}`).current;
  const [el, setEl] = useState<HTMLDialogElement | null>(null);
  useEffect(() => { setEl(ref.current); }, []);
  useVisualViewport(el, open);
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) {
      opener.current = document.activeElement;
      if (typeof d.showModal === 'function') d.showModal(); else d.setAttribute('open', '');
      // R3-U24: initial focus is the first field (at 320px the UA's pick could land on the sheet itself or a
      // footer button scrolled out of view); a dialog without fields keeps the UA's choice. V98-4 review T4-H4:
      // a multi-field editor focuses its heading instead.
      const first = initialFocus === 'title' ? d.querySelector<HTMLElement>(`#${titleId}`)
        : d.querySelector<HTMLElement>('.gp-dialog-body input:not([type="hidden"]):not([disabled]), .gp-dialog-body select:not([disabled]), .gp-dialog-body textarea:not([disabled])');
      first?.focus();
    }
    if (!open && d.open) {
      if (typeof d.close === 'function') d.close(); else d.removeAttribute('open');
      // Return focus to the opener unless something else already took it (U12).
      const back = opener.current as HTMLElement | null;
      const lost = !document.activeElement || document.activeElement === document.body || d.contains(document.activeElement);
      if (lost && back && back.isConnected && typeof back.focus === 'function') back.focus();
    }
  }, [open]);
  // A new step is announced by moving focus to its step line (U9).
  const lastStep = useRef(step?.n);
  useEffect(() => {
    if (!open || !step) return;
    if (lastStep.current !== undefined && lastStep.current !== step.n) stepRef.current?.focus();
    lastStep.current = step.n;
  }, [open, step?.n]);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (busy || submitDisabled) return;
    await onSubmit();
  };
  // V114-5 (panel H1): the subtitle (a decline's consequence, a delete's effect) describes the dialog, so it reaches screen readers before confirm.
  return (
    <dialog ref={ref} className="gp-dialog" aria-labelledby={titleId} aria-describedby={subtitle ? `${titleId}-sub` : undefined} onCancel={(e) => { e.preventDefault(); onClose(); }}>
      {open ? (
        <form className="gp-dialog-form" onSubmit={submit} noValidate>
          <div className="gp-dialog-head">
            <h2 id={titleId} tabIndex={-1}>{title}</h2>
            {subtitle ? <p id={`${titleId}-sub`} className="gp-dialog-sub">{subtitle}</p> : null}
            {headerBack ? <button type="button" className="gp-linkbtn gp-linkbtn--compact gp-dialog-back" onClick={headerBack.onClick}>{headerBack.label}</button> : null}
            {step ? <p className="gp-dialog-step" ref={stepRef} tabIndex={-1} data-step={step.n}>{`Step ${step.n} of ${step.of} · ${step.label}`}</p> : null}
            {error ? <Alert variant="error">{error}</Alert> : null}
          </div>
          <div className="gp-dialog-body gp-form">{children}</div>
          {pinned ? <div className="gp-dialog-pinned">{pinned}</div> : null}
          <div className="gp-dialog-foot gp-actions gp-actions--end" data-part="dialog-footer">
            {back ? <Button variant="ghost" onClick={back.onClick}>{back.label}</Button> : null}
            <Button onClick={onClose}>{cancelLabel}</Button>
            {secondary ? <Button aria-disabled={secondary.busy || undefined} onClick={() => { if (!secondary.busy) secondary.onClick(); }}>{secondary.busy ? 'Saving…' : secondary.label}</Button> : null}
            {hideSubmit ? null : (
              <Button type="submit" variant={danger ? 'danger' : 'primary'} aria-disabled={busy || submitDisabled || undefined} aria-describedby={describedBy}>{busy ? 'Saving…' : submitLabel}</Button>
            )}
          </div>
        </form>
      ) : null}
    </dialog>
  );
}

/** Message from an API error (the server's detail when present). */
export function errorText(e: unknown, fallback: string): string {
  return e instanceof Error && e.message ? e.message : fallback;
}
